import { useState, useCallback, useRef } from 'react';

// Debounce times in milliseconds for different event types
const DEBOUNCE_TIMES = {
  'copy-attempt': 500,
  'paste-attempt': 500,
  'right-click-attempt': 200,
  'window-blur': 1000,
  'tab-switch': 1000,
  'devtools-open': 10000,
  'print-attempt': 1000,
  'save-page-attempt': 1000,
  'inspect-element-attempt': 2000,
  'screen-recording-attempt': 2000,
  'mobile-device-access': 10000,
  'default': 200
};

/**
 * Custom hook for handling cheating detection events with debouncing
 * @returns {Object} Object containing cheatingEvents array and handleCheatingDetected function
 */
export const useCheatingDetection = () => {
  const [cheatingEvents, setCheatingEvents] = useState([]);
  const lastEventTimestamps = useRef({});
  const pendingTimeouts = useRef({});

  const handleCheatingDetected = useCallback((event) => {
    const eventType = event.type;
    const content = event.content || '';
    const eventKey = `${eventType}-${content}`;
    const now = Date.now();
    
    // Get debounce time for this event type
    const debounceTime = DEBOUNCE_TIMES[eventType] || DEBOUNCE_TIMES.default;
    
    // Check if we should skip this event due to recent duplicate
    const lastEventTime = lastEventTimestamps.current[eventKey];
    if (lastEventTime && (now - lastEventTime) < debounceTime) {
      return; // Skip duplicate event
    }
    
    // Clear any existing timeout for this event type
    if (pendingTimeouts.current[eventKey]) {
      clearTimeout(pendingTimeouts.current[eventKey]);
    }
    
    // Set a small delay to batch rapid events
    pendingTimeouts.current[eventKey] = setTimeout(() => {
      // Update timestamp
      lastEventTimestamps.current[eventKey] = now;
      
      // Add event to state
      setCheatingEvents(prevEvents => {
        const newEvents = [...prevEvents, event];
        console.log('Cheating event saved:', event.type, event.content ? `"${event.content.substring(0, 50)}..."` : '(no content)');
        return newEvents;
      });
      
      // Clean up timeout reference
      delete pendingTimeouts.current[eventKey];
    }, 50); // Small 50ms delay to batch rapid events
    
  }, []);

  // Cleanup function to clear all pending timeouts
  const cleanup = useCallback(() => {
    Object.values(pendingTimeouts.current).forEach(timeout => {
      clearTimeout(timeout);
    });
    pendingTimeouts.current = {};
  }, []);
  
  return { 
    cheatingEvents, 
    handleCheatingDetected,
    cleanup
  };
};
